// Static demo-mode fill-in data for Leave.component.jsx.
// Shapes must match exactly what this view's own rendering expects — see
// LeaveComponent's `leaveRequestsList`/`leaveData`/`companyManagers` state and
// Leave.config.jsx's `columnsConfig` cell renderers (which read `row.*` per
// request and resolve `row.assignee` against `companyManagers`).

// Firestore Timestamp-like stub — `row.requestDate.toDate()` is called
// directly (no optional chaining) by Leave.config.jsx's "Report Date" column,
// so this needs a real `toDate()` method, not a plain Date.
const toTimestamp = (isoString) => ({
  toDate: () => new Date(isoString),
});

// Anchored to "today" so the request/report dates never go stale — a
// hardcoded past date would drift further behind every day the demo stays
// live (see UserAttendance.demoData.js for the same pattern).
const DAY_MS = 24 * 60 * 60 * 1000;
const daysFromNow = (n) => new Date(Date.now() + n * DAY_MS);
const isoAt = (daysOffset, hh, mm) => {
  const d = daysFromNow(daysOffset);
  d.setUTCHours(hh, mm, 0, 0);
  return d.toISOString();
};
const leaveDateAt = (daysOffset) => {
  const d = daysFromNow(daysOffset);
  return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
};

// Consumed by Leave.config.jsx's `columnsConfig` cellRenderers: comment, type,
// leaveDate ("YYYY-M-D" string), requestDate (Timestamp, via .toDate()),
// assignee (resolved against companyManagers' userUID), teamId, status.
// `type` values match Leave.constants.js (paidLeaveTypeList + unpaid leave).
export const DEMO_LEAVE_REQUESTS_LIST = [
  {
    id: 'demo-leave-1',
    comment: 'Family function out of town',
    type: 'Planned Leave',
    leaveDate: leaveDateAt(-10),
    requestDate: toTimestamp(isoAt(-20, 9, 0)),
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    status: 'Approved',
  },
  {
    id: 'demo-leave-2',
    comment: 'Fever and cold, resting at home',
    type: 'Sick Leave',
    leaveDate: leaveDateAt(-34),
    requestDate: toTimestamp(isoAt(-35, 8, 30)),
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    status: 'Approved',
  },
  {
    id: 'demo-leave-3',
    comment: 'Personal errand, back next day',
    type: 'Casual Leave',
    leaveDate: leaveDateAt(5),
    requestDate: toTimestamp(isoAt(-3, 11, 15)),
    assignee: 'demo-manager-2',
    teamId: 'demo-team-engineering',
    status: 'Pending',
  },
  {
    id: 'demo-leave-4',
    comment: 'Doctor appointment',
    type: 'Unpaid Leave',
    leaveDate: leaveDateAt(-72),
    requestDate: toTimestamp(isoAt(-75, 7, 45)),
    assignee: 'demo-manager-1',
    teamId: 'demo-team-engineering',
    status: 'Rejected',
  },
  {
    id: 'demo-leave-5',
    comment: 'Wedding anniversary trip',
    type: 'Planned Leave',
    leaveDate: leaveDateAt(25),
    requestDate: toTimestamp(isoAt(-1, 10, 0)),
    assignee: 'demo-manager-2',
    teamId: 'demo-team-engineering',
    status: 'Pending',
  },
];

// Consumed directly by LeaveComponent's render (`leaveBalanceData.map` reads
// `leaveData[item.key]`) and by the "Add Leave Request" button's `disabled`
// check (`leaveData.loading`). effectiveRemaining = remaining(12) - pending(2).
export const DEMO_LEAVE_DATA = {
  loading: false,
  totalAllocatedLeaves: 18,
  usedLeaves: 6,
  availableLeaves: 10,
  pendingRequests: 2,
};

// Consumed by Leave.config.jsx's "Manager" column (`companyManagers.find(a
// => a.userUID === row?.assignee)`) and by LeaveFormModal/UpdateLeaveFormModal
// dropdowns — matches the shape produced by getTeamMemberDetails.
export const DEMO_COMPANY_MANAGERS = [
  { userUID: 'demo-manager-1', userName: 'Rachel Turner' },
  { userUID: 'demo-manager-2', userName: 'Emma Williams' },
];
